import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Donors.jsx — renamed to Member
// throughout, matching the backend's rename (Donor -> Member, `/donors` ->
// `/members`). Unlike Supplier/Customer, a Member's identity fields
// (first_name/email/mobile/...) live on the linked `user`, not on the
// Member row itself — MemberController::present() flattens them onto the
// top-level JSON, so this page reads them the same way the source did.
interface MemberRow {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  mobile: string | null;
  member_type: string | null;
  member_since: string | null;
}

export function Members() {
  const { lists } = useConfig();
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');
  const [memberType, setMemberType] = useState('');

  const canCreate = can('memberships.create');
  const canUpdate = can('memberships.update');
  const canArchive = can('memberships.archive');

  const filters = useMemo(
    () => ({ search: search || undefined, member_type: memberType || undefined }),
    [search, memberType]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<MemberRow>('/members', filters);

  const remove = async (member: MemberRow) => {
    if (!(await confirmDelete(`Delete ${member.first_name} ${member.last_name}?`))) return;
    try {
      await api.delete(`/members/${member.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<MemberRow>(
      api,
      '/members',
      { search: search || undefined, member_type: memberType || undefined },
      [
        { label: 'First Name', value: (r) => r.first_name },
        { label: 'Last Name', value: (r) => r.last_name },
        { label: 'Email', value: (r) => r.email },
        { label: 'Mobile', value: (r) => r.mobile },
        { label: 'Member Type', value: (r) => labelFor(lists, 'member_type', r.member_type ?? '') },
        { label: 'Member Since', value: (r) => r.member_since },
      ],
      'members.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Members</h1>
        {canCreate && (
          <Link className="btn btn-primary" to="/memberships/new">
            + Add
          </Link>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name, email or mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={memberType} onChange={(e) => setMemberType(e.target.value)}>
          <option value="">All types</option>
          {listOptions(lists, 'member_type').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <strong>
                        {m.first_name} {m.last_name}
                      </strong>
                    </td>
                    <td>{m.email}</td>
                    <td>{labelFor(lists, 'member_type', m.member_type ?? '')}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/memberships/${m.id}`} />
                      {canUpdate && (
                        <IconButton icon={IconEdit} label="Edit" to={`/memberships/${m.id}/edit`} />
                      )}
                      {canArchive && (
                        <IconButton
                          icon={IconTrash}
                          label="Delete"
                          variant="danger-ghost"
                          onClick={() => remove(m)}
                        />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="muted center">
                    No members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
